import { useCallback, useEffect, useState } from "react";
import api, { apiError, can } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Plus, Pencil, Trash2, Copy, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";

const EMPTY_TYPE = { name: "", default_duration: 30, min_duration: "", max_duration: "", buffer_minutes: 0, requires_operator: true, operator_qualification: "", active: true };
const EMPTY_MACHINE = { name: "", type_id: "", code: "", brand: "", model: "", serial: "", room: "", status: "attivo", notes: "", purchase_date: "", last_maintenance: "", next_maintenance: "", requires_operator: null, default_duration: "", active: true };

export default function Machines() {
  const { user } = useAuth();
  const canCreate = can(user, "macchinari", "create");
  const canEdit = can(user, "macchinari", "edit");
  const canDelete = can(user, "macchinari", "delete");
  const canManage = canCreate || canEdit || canDelete;
  const [board, setBoard] = useState(null);
  const [types, setTypes] = useState([]);
  const [machines, setMachines] = useState([]);
  const [editType, setEditType] = useState(null);
  const [editMachine, setEditMachine] = useState(null);
  const [typeForm, setTypeForm] = useState(EMPTY_TYPE);
  const [machineForm, setMachineForm] = useState(EMPTY_MACHINE);

  const load = useCallback(async () => {
    const [{ data: b }, { data: t }, { data: m }] = await Promise.all([
      api.get("/machines/status/board"), api.get("/machine-types"), api.get("/machines"),
    ]);
    setBoard(b); setTypes(t); setMachines(m);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const saveType = async () => {
    if (!typeForm.name.trim()) { toast.error("Nome obbligatorio"); return; }
    const body = { ...typeForm, min_duration: typeForm.min_duration || null, max_duration: typeForm.max_duration || null };
    try {
      if (editType?.id) await api.put(`/machine-types/${editType.id}`, body);
      else await api.post("/machine-types", body);
      toast.success("Tipologia salvata"); setEditType(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveMachine = async () => {
    if (!machineForm.name.trim() || !machineForm.type_id) { toast.error("Nome e tipologia obbligatori"); return; }
    const body = { ...machineForm, default_duration: machineForm.default_duration || null };
    try {
      if (editMachine?.id) await api.put(`/machines/${editMachine.id}`, body);
      else await api.post("/machines", body);
      toast.success("Macchinario salvato"); setEditMachine(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const dupType = async (t) => { await api.post(`/machine-types/${t.id}/duplicate`); toast.success("Tipologia duplicata"); load(); };
  const dupMachine = async (m) => { await api.post(`/machines/${m.id}/duplicate`); toast.success("Macchinario duplicato"); load(); };
  const delType = async (t) => { if (window.confirm(`Eliminare la tipologia "${t.name}"? Le unità verranno disattivate.`)) { await api.delete(`/machine-types/${t.id}`); load(); } };
  const delMachine = async (m) => { if (window.confirm(`Eliminare "${m.name}"?`)) { await api.delete(`/machines/${m.id}`); load(); } };

  return (
    <div className="space-y-6" data-testid="machines-page">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="font-heading text-2xl font-bold">Macchinari</h1>
        <div className="ml-auto flex gap-2">
          <button data-testid="machines-refresh" onClick={load} className="p-2.5 rounded-xl bg-white border border-slate-200"><RefreshCw size={15} /></button>
          {canCreate && <>
            <button data-testid="machine-type-new" onClick={() => { setTypeForm(EMPTY_TYPE); setEditType({}); }} className="flex items-center gap-1.5 bg-white border border-slate-200 px-4 py-2.5 rounded-full text-sm font-medium hover:border-cyan-300"><Plus size={15} /> Tipologia</button>
            <button data-testid="machine-new" onClick={() => { setMachineForm(EMPTY_MACHINE); setEditMachine({}); }} className="flex items-center gap-1.5 bg-blue-600 text-white px-4 py-2.5 rounded-full text-sm font-medium"><Plus size={15} /> Macchinario</button>
          </>}
        </div>
      </div>

      {/* Stato in tempo reale */}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="machines-board">
        {(board?.groups || []).map((g) => (
          <div key={g.type.id} className="bg-white rounded-2xl border border-slate-100 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-bold uppercase text-sm tracking-wide">{g.type.name}</h3>
              <span className="text-xs text-slate-400">{g.units.length} unità</span>
            </div>
            <div className="space-y-2">
              {g.units.map((u) => (
                <div key={u.id} data-testid={`board-unit-${u.id}`} className="flex items-center gap-2.5 bg-slate-50/60 rounded-xl px-3 py-2.5">
                  <span className={`w-3 h-3 rounded-full shrink-0 ${u.state === "disponibile" ? "bg-emerald-500" : u.state === "in_uso" ? "bg-red-500" : "bg-amber-500"}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{u.name}</p>
                    <p className="text-xs text-slate-400">
                      {u.state === "disponibile" && `Disponibile${u.room ? ` · ${u.room}` : ""}`}
                      {u.state === "in_uso" && `In uso: ${u.state_detail?.patient} · fino alle ${u.state_detail?.until}`}
                      {u.state === "manutenzione" && "In manutenzione"}
                      {u.state === "disattivato" && "Disattivato"}
                    </p>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${u.effective_requires_operator ? "bg-violet-50 text-violet-600" : "bg-slate-100 text-slate-500"}`}>
                    {u.effective_requires_operator ? "con operatore" : "autonomo"}
                  </span>
                </div>
              ))}
              {g.units.length === 0 && <p className="text-xs text-slate-400">Nessuna unità registrata.</p>}
            </div>
          </div>
        ))}
      </div>

      {/* Gestione macchinari secondo permessi */}
      {canManage && (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid="types-admin">
            <h3 className="font-heading font-bold mb-3">Tipologie macchinari</h3>
            <div className="space-y-2">
              {types.map((t) => (
                <div key={t.id} data-testid={`type-row-${t.id}`} className="flex items-center gap-2 border border-slate-100 rounded-xl px-3 py-2.5">
                  <div className="flex-1">
                    <p className="text-sm font-medium">{t.name}</p>
                    <p className="text-xs text-slate-400">{t.default_duration} min · {t.requires_operator ? "operatore obbligatorio" : "senza operatore"}{t.buffer_minutes ? ` · pausa ${t.buffer_minutes} min` : ""}</p>
                  </div>
                  {canCreate && <button data-testid={`type-dup-${t.id}`} onClick={() => dupType(t)} className="p-1.5 text-slate-400 hover:text-blue-600"><Copy size={14} /></button>}
                  {canEdit && <button data-testid={`type-edit-${t.id}`} onClick={() => { setTypeForm({ ...EMPTY_TYPE, ...t }); setEditType(t); }} className="p-1.5 text-slate-400 hover:text-blue-600"><Pencil size={14} /></button>}
                  {canDelete && <button data-testid={`type-del-${t.id}`} onClick={() => delType(t)} className="p-1.5 text-slate-400 hover:text-red-500"><Trash2 size={14} /></button>}
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid="units-admin">
            <h3 className="font-heading font-bold mb-3">Unità registrate</h3>
            <div className="space-y-2">
              {machines.map((m) => (
                <div key={m.id} data-testid={`machine-row-${m.id}`} className="flex items-center gap-2 border border-slate-100 rounded-xl px-3 py-2.5">
                  <div className="flex-1">
                    <p className="text-sm font-medium">{m.name} <span className="text-xs text-slate-400">({m.type_name})</span></p>
                    <p className="text-xs text-slate-400">{[m.code, m.room, m.brand].filter(Boolean).join(" · ") || "—"} · {m.status}</p>
                  </div>
                  {canCreate && <button data-testid={`machine-dup-${m.id}`} onClick={() => dupMachine(m)} className="p-1.5 text-slate-400 hover:text-blue-600"><Copy size={14} /></button>}
                  {canEdit && <button data-testid={`machine-edit-${m.id}`} onClick={() => { setMachineForm({ ...EMPTY_MACHINE, ...m, default_duration: m.default_duration || "" }); setEditMachine(m); }} className="p-1.5 text-slate-400 hover:text-blue-600"><Pencil size={14} /></button>}
                  {canDelete && <button data-testid={`machine-del-${m.id}`} onClick={() => delMachine(m)} className="p-1.5 text-slate-400 hover:text-red-500"><Trash2 size={14} /></button>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Dialog tipologia */}
      <Dialog open={Boolean(editType)} onOpenChange={(o) => !o && setEditType(null)}>
        <DialogContent className="max-w-md rounded-2xl" data-testid="type-modal">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editType?.id ? "Modifica tipologia" : "Nuova tipologia"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome *</Label><Input data-testid="type-name" value={typeForm.name} onChange={(e) => setTypeForm({ ...typeForm, name: e.target.value })} className="rounded-xl" /></div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label>Durata std (min)</Label><Input data-testid="type-duration" type="number" value={typeForm.default_duration} onChange={(e) => setTypeForm({ ...typeForm, default_duration: Number(e.target.value) })} className="rounded-xl" /></div>
              <div><Label>Min</Label><Input data-testid="type-min" type="number" value={typeForm.min_duration} onChange={(e) => setTypeForm({ ...typeForm, min_duration: e.target.value ? Number(e.target.value) : "" })} className="rounded-xl" /></div>
              <div><Label>Max</Label><Input data-testid="type-max" type="number" value={typeForm.max_duration} onChange={(e) => setTypeForm({ ...typeForm, max_duration: e.target.value ? Number(e.target.value) : "" })} className="rounded-xl" /></div>
            </div>
            <div><Label>Pausa tecnica tra utilizzi (min)</Label><Input data-testid="type-buffer" type="number" value={typeForm.buffer_minutes} onChange={(e) => setTypeForm({ ...typeForm, buffer_minutes: Number(e.target.value) })} className="rounded-xl" /></div>
            <div className="flex items-center gap-2"><Switch data-testid="type-requires-operator" checked={typeForm.requires_operator} onCheckedChange={(c) => setTypeForm({ ...typeForm, requires_operator: c })} /><Label>Richiede operatore</Label></div>
            <div><Label>Qualifica operatore (opz.)</Label><Input data-testid="type-qualification" value={typeForm.operator_qualification} onChange={(e) => setTypeForm({ ...typeForm, operator_qualification: e.target.value })} className="rounded-xl" /></div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" data-testid="type-cancel" onClick={() => setEditType(null)} className="rounded-full">Annulla</Button>
            <Button data-testid="type-save" onClick={saveType} className="rounded-full">Salva</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog macchinario */}
      <Dialog open={Boolean(editMachine)} onOpenChange={(o) => !o && setEditMachine(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl" data-testid="machine-modal">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editMachine?.id ? "Modifica macchinario" : "Nuovo macchinario"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Nome *</Label><Input data-testid="machine-name" value={machineForm.name} onChange={(e) => setMachineForm({ ...machineForm, name: e.target.value })} className="rounded-xl" /></div>
            <div>
              <Label>Tipologia *</Label>
              <Select value={machineForm.type_id} onValueChange={(v) => setMachineForm({ ...machineForm, type_id: v })}>
                <SelectTrigger data-testid="machine-type" className="rounded-xl"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>{types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Codice interno</Label><Input data-testid="machine-code" value={machineForm.code} onChange={(e) => setMachineForm({ ...machineForm, code: e.target.value })} className="rounded-xl" /></div>
            <div><Label>Sala / posizione</Label><Input data-testid="machine-room" value={machineForm.room} onChange={(e) => setMachineForm({ ...machineForm, room: e.target.value })} className="rounded-xl" /></div>
            <div><Label>Marca</Label><Input data-testid="machine-brand" value={machineForm.brand} onChange={(e) => setMachineForm({ ...machineForm, brand: e.target.value })} className="rounded-xl" /></div>
            <div><Label>Modello</Label><Input data-testid="machine-model" value={machineForm.model} onChange={(e) => setMachineForm({ ...machineForm, model: e.target.value })} className="rounded-xl" /></div>
            <div><Label>Numero seriale</Label><Input data-testid="machine-serial" value={machineForm.serial} onChange={(e) => setMachineForm({ ...machineForm, serial: e.target.value })} className="rounded-xl" /></div>
            <div>
              <Label>Stato</Label>
              <Select value={machineForm.status} onValueChange={(v) => setMachineForm({ ...machineForm, status: v })}>
                <SelectTrigger data-testid="machine-status" className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="attivo">Attivo</SelectItem>
                  <SelectItem value="manutenzione">Manutenzione</SelectItem>
                  <SelectItem value="disattivato">Disattivato</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Data acquisto</Label><Input data-testid="machine-purchase" type="date" value={machineForm.purchase_date} onChange={(e) => setMachineForm({ ...machineForm, purchase_date: e.target.value })} className="rounded-xl" /></div>
            <div><Label>Ultima manutenzione</Label><Input data-testid="machine-last-maint" type="date" value={machineForm.last_maintenance} onChange={(e) => setMachineForm({ ...machineForm, last_maintenance: e.target.value })} className="rounded-xl" /></div>
            <div><Label>Prossima manutenzione</Label><Input data-testid="machine-next-maint" type="date" value={machineForm.next_maintenance} onChange={(e) => setMachineForm({ ...machineForm, next_maintenance: e.target.value })} className="rounded-xl" /></div>
            <div>
              <Label>Richiede operatore</Label>
              <Select value={machineForm.requires_operator === null ? "inherit" : machineForm.requires_operator ? "yes" : "no"}
                onValueChange={(v) => setMachineForm({ ...machineForm, requires_operator: v === "inherit" ? null : v === "yes" })}>
                <SelectTrigger data-testid="machine-requires-operator" className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="inherit">Come tipologia</SelectItem>
                  <SelectItem value="yes">Sì</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Note</Label><Input data-testid="machine-notes" value={machineForm.notes} onChange={(e) => setMachineForm({ ...machineForm, notes: e.target.value })} className="rounded-xl" /></div>
            <div className="flex items-center gap-2"><Switch data-testid="machine-active" checked={machineForm.active} onCheckedChange={(c) => setMachineForm({ ...machineForm, active: c })} /><Label>Attivo</Label></div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" data-testid="machine-cancel" onClick={() => setEditMachine(null)} className="rounded-full">Annulla</Button>
            <Button data-testid="machine-save" onClick={saveMachine} className="rounded-full">Salva</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
